// Water surface rendering and depth/tide lookups.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.

import * as THREE from 'three';
import { HEX_R, HEX_H, SEA_LEVEL, hexToWorld, terrainHeightAt, worldToHex, valueNoise2D, getTerrainSeed } from './terrain';
import { washTile, toTexture } from './wash';

export interface WaterTier {
  opacity: number;
  color: number;
}

// Turquoise in the shallows running to cobalt in the deep, as the
// reference reads it — the old set went pale blue to navy, which lost the
// tropical shelf colour entirely.
export const WATER_TIERS: readonly WaterTier[] = [
  { opacity: 0.52, color: 0x8ccadd },
  { opacity: 0.62, color: 0x59aed0 },
  { opacity: 0.74, color: 0x3d8fbd },
  { opacity: 0.88, color: 0x2d6098 },
];

// Each tier's own wash: overlapping pigment blooms, so a stretch of sea
// mottles like laid-down paint instead of reading as flat plastic.
const WATER_WASHES: readonly { base: string; pigs: string[]; edge: string; grain: string }[] = [
  { base: '#a5dcea', pigs: ['#8ccadd', '#c2e6f2', '#7dbdd6'], edge: '#6aa9c4', grain: '#5b9ab6' },
  { base: '#6fbedb', pigs: ['#59aed0', '#8ccfe6', '#4a9cc2'], edge: '#3f8cb4', grain: '#357fa6' },
  { base: '#4f9ac4', pigs: ['#3d8fbd', '#6fb0d3', '#347bae'], edge: '#2c72a2', grain: '#256892' },
  { base: '#3a6ea3', pigs: ['#2d6098', '#4d82b4', '#26538a'], edge: '#1f4a7e', grain: '#1a3f6d' },
];

export const WATER_SURF_Y = SEA_LEVEL * HEX_H + HEX_H * 0.2;

// Lambert, not Phong: a specular highlight on flat water reads as plastic,
// and watercolour has no such thing. Lambert still darkens with the
// day/night cycle, which a Basic material wouldn't.
const waterMats = WATER_TIERS.map((t, i) => {
  const w = WATER_WASHES[i];
  const m = new THREE.MeshLambertMaterial({
    color: t.color, transparent: true, opacity: t.opacity, side: THREE.DoubleSide,
  });
  m.map = toTexture(washTile(w.base, w.pigs, w.edge, w.grain));
  m.map.repeat.set(0.7, 0.7);
  return m;
});

// Water is a single flat surface sheet per column (a plain hexagon with
// no side walls), so transparency looks continuous across the lake —
// no internal faces or seams. Depth shading comes from how deep the
// lakebed is below, and the actual bed is visible through the surface.
const waterSurfGeo = new THREE.CircleGeometry(HEX_R * 1.002, 6);
waterSurfGeo.rotateX(-Math.PI / 2);

// Whether the hex grid stays drawn across the water. The reference keeps it
// legible right out to sea, but open water may read better unbroken — set
// false to let the sea run as one continuous wash.
const WATER_SEAMS = false;

// Pale seam around each water tile, matching the cream rim on the land hexes.
const waterSeamMat = new THREE.LineBasicMaterial({ color: 0xeaf5f2, transparent: true, opacity: 0.5 });
const waterSeamGeo = new THREE.BufferGeometry();
{
  const pts: number[] = [];
  for (let i = 0; i < 6; i++) {
    const a0 = (Math.PI / 3) * i, a1 = (Math.PI / 3) * (i + 1);
    pts.push(Math.cos(a0) * HEX_R, 0, Math.sin(a0) * HEX_R);
    pts.push(Math.cos(a1) * HEX_R, 0, Math.sin(a1) * HEX_R);
  }
  waterSeamGeo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
}

function waterMaterialForDepth(depth: number): THREE.MeshLambertMaterial {
  return waterMats[Math.min(Math.max(depth - 1, 0), waterMats.length - 1)];
}

// ================= Ripple rings =================
// Pale concentric rings where something breaks the surface. With the hex
// seams off across the water these are also what gives open sea a sense of
// scale, so they earn their place beyond decoration.
//
// Unlit and depth-write-free: they lie flat on the surface and must not
// occlude the bed showing through it.
// Ring bands as fractions of the outer radius. Deliberately hairline and
// getting finer outward: a thick band reads as a painted stripe round the
// object, whereas a few thin lines thinning as they spread is what the eye
// takes for a ripple. The thinning is what fakes the fade — no alpha ramp
// needed, since a narrower band simply covers fewer pixels.
// All three sit outside the object rather than starting under it — the
// innermost used to run at 0.56 of the radius, well inside the rock, so its
// arc crossed the thing it was meant to be rippling around.
const RIPPLE_BANDS = [[0.72, 0.765], [0.87, 0.905], [0.99, 1.012]] as const;

// Merges the bands into one geometry so a ripple is a single mesh (and, in
// the instanced pool, a single instance). Built by concatenating
// non-indexed rings rather than pulling in BufferGeometryUtils.
export function makeRippleGeometry(radius: number): THREE.BufferGeometry {
  const parts: Float32Array[] = [];
  let total = 0;
  RIPPLE_BANDS.forEach(([a, b]) => {
    const g = new THREE.RingGeometry(a * radius, b * radius, 32).toNonIndexed();
    g.rotateX(-Math.PI / 2);
    const p = g.getAttribute('position').array as Float32Array;
    parts.push(p);
    total += p.length;
  });
  const merged = new Float32Array(total);
  let off = 0;
  parts.forEach((p) => { merged.set(p, off); off += p.length; });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(merged, 3));
  geo.computeVertexNormals();
  return geo;
}

// Shared by the standing ripples — rocks and wrecks — so one opacity tween
// breathes all of them at once, rather than rewriting hundreds of instance
// matrices every frame.
const RIPPLE_OPACITY = 0.3;
export const rippleMat = new THREE.MeshBasicMaterial({
  color: 0xeaf7fa, transparent: true, opacity: RIPPLE_OPACITY, side: THREE.DoubleSide, depthWrite: false,
});

export function makeRippleRings(radius: number, mat: THREE.Material = rippleMat): THREE.Group {
  const g = new THREE.Group();
  g.add(new THREE.Mesh(makeRippleGeometry(radius), mat));
  return g;
}

// The boat's own ripple. It can't be a child of the hull — the hull bobs
// and rolls, and these have to stay flat on the water — so it rides along
// as a sibling that main.ts positions each frame. Its own material, so it
// can pulse harder without dragging every standing ring along with it.
const boatRippleMat = rippleMat.clone();
// Sized to the hull: built at its half-length and squashed across to match
// the beam, so the rings run as an elongated wake rather than a circle
// around a long narrow boat. Hardcoded rather than derived — building.ts
// already imports this module, so reaching back for its scale would close
// an import cycle.
const BOAT_RIPPLE_SQUASH = 0.32;
export const boatRipple = makeRippleRings(0.62, boatRippleMat);
boatRipple.visible = false;

// heading must match the hull's, since the rings are elongated along the
// boat now — an unrotated wake would sit across a boat pointing north.
export function updateBoatRipple(x: number, z: number, heading: number, now: number, afloat: boolean): void {
  boatRipple.visible = afloat;
  if (!afloat) return;
  boatRipple.position.set(x, WATER_SURF_Y + 0.004 + swellAt(x, z, now), z);
  boatRipple.rotation.y = -heading;
  // Rings spread and fade, then start over — the boat pushing water out
  // rather than sitting in a fixed halo. The quick ramp at the start of the
  // cycle hides the reset.
  const cycle = (now * 0.00084) % 1;
  const spread = 0.82 + cycle * 0.42;
  boatRipple.scale.set(spread, 1, spread * BOAT_RIPPLE_SQUASH);
  boatRippleMat.opacity = 0.36 * (1 - cycle) * Math.min(1, cycle / 0.08);
}

// Builds a positioned water surface mesh for a hex column; the caller is
// responsible for registering/unregistering it with the chunk streaming
// system (mirrors legacy addWaterSurface, minus that bookkeeping).
export function createWaterSurfaceMesh(q: number, r: number): THREE.Mesh {
  const depth = SEA_LEVEL - terrainHeightAt(q, r); // 1 = shallow
  const mat = waterMaterialForDepth(depth);
  const pos = hexToWorld(q, r);
  const mesh = new THREE.Mesh(waterSurfGeo, mat);
  mesh.position.set(pos.x, WATER_SURF_Y, pos.z);
  mesh.userData.isWater = true;
  if (WATER_SEAMS) mesh.add(new THREE.LineSegments(waterSeamGeo, waterSeamMat));
  return mesh;
}

// Per-frame shimmer on the water materials' opacity; call once per animate() tick.
// ================= Swell =================
// How rough the sea is, 0..1, set once per frame from the weather. Held
// here rather than threaded through every caller, since the boat, its
// ripple and the tiles themselves all need to agree on it.
let choppiness = 0;
export function setChoppiness(c: number): void {
  choppiness = Math.max(0, Math.min(1, c));
}

// Kept small on purpose: the boat's sole sits about 0.010 above the
// waterline, so a bigger swell would wash over the floor of an undamaged
// boat. The boat rides this same function instead of fighting it.
const SWELL_AMPLITUDE = HEX_H * 0.07;

// A function of world position, not of tile — two crossed waves rolling
// across the map. Sampling per-tile would let neighbours drift apart and
// tear the surface open at the seams.
export function swellAt(x: number, z: number, now: number): number {
  if (choppiness <= 0) return 0;
  const t = now * 0.0012;
  return (Math.sin(x * 1.7 + t) + Math.sin(z * 2.3 + t * 1.31)) * 0.5 * SWELL_AMPLITUDE * choppiness;
}

// Pitch and roll to match the wave's slope under a hull at (x, z).
//
// The raw gradient is tiny — the swell is only a centimetre or so tall over
// a wavelength of several units — so a hull following it exactly would
// barely move. TILT_GAIN exaggerates the angle without touching the height,
// which is a lie physically but is what actually reads as riding a sea.
const TILT_GAIN = 7;
const TILT_SAMPLE = 0.3;
export function swellTilt(x: number, z: number, heading: number, now: number): { pitch: number; roll: number } {
  if (choppiness <= 0) return { pitch: 0, roll: 0 };
  const d = TILT_SAMPLE;
  const gx = (swellAt(x + d, z, now) - swellAt(x - d, z, now)) / (2 * d);
  const gz = (swellAt(x, z + d, now) - swellAt(x, z - d, now)) / (2 * d);
  // Project the world gradient onto the hull's own axes. With the mesh
  // yawed by -heading, its bow runs along (cos, sin) and starboard along
  // (-sin, cos).
  const ch = Math.cos(heading), sh = Math.sin(heading);
  const fore = gx * ch + gz * sh;
  const stbd = -gx * sh + gz * ch;
  return { pitch: fore * TILT_GAIN, roll: -stbd * TILT_GAIN };
}

export function updateWaterShimmer(now: number): void {
  const shimmer = Math.sin(now * 0.0012) * 0.04;
  waterMats.forEach((m, i) => { m.opacity = WATER_TIERS[i].opacity + shimmer; });
  // Standing ripples breathe on a slower, offset beat, so they don't rise
  // and fall in lockstep with the water beneath them.
  rippleMat.opacity = RIPPLE_OPACITY + Math.sin(now * 0.00096 + 1.7) * 0.09;
}

export interface WaterInfo {
  bedTopY: number;
  surfaceY: number;
}

export function waterInfoAt(x: number, z: number): WaterInfo | null {
  const hx = worldToHex(x, z);
  const h = terrainHeightAt(hx.q, hx.r);
  if (h >= SEA_LEVEL) return null;
  return { bedTopY: h * HEX_H + HEX_H / 2, surfaceY: WATER_SURF_Y };
}

// The shallowest water band (one level below sea level) — shore fishing
// spots, and (see systems/building.ts's canWade) the only water depth the
// fisherman can wade into on foot.
export function isShallowWaterAt(x: number, z: number): boolean {
  const hx = worldToHex(x, z);
  return terrainHeightAt(hx.q, hx.r) === SEA_LEVEL - 1;
}

// Wading is noticeably slower than walking on dry land or a jetty deck.
export const WADE_SPEED_MULT = 0.55;

export type Habitat = 'deep' | 'coral' | 'mangrove' | 'shellfish' | 'open';

// Lets world/fixedMap.ts assign specific habitats to specific landmasses
// (e.g. "this knoll's shallows are exclusively coral") instead of the
// noise-based patches below. null (the default) leaves every procedural
// world's habitat placement untouched; returning null for a given hex
// from the override function itself falls through to the normal rule too
// (used for the 'deep' tier, which stays depth-based everywhere).
let habitatOverride: ((q: number, r: number) => Habitat | null) | null = null;

export function setHabitatOverride(fn: ((q: number, r: number) => Habitat | null) | null): void {
  habitatOverride = fn;
}

// Each water tile belongs to a habitat, decided deterministically: deep
// water (3+ layers down), mangrove shallows and coral pools (noise
// patches in the shallows), or open water.
export function habitatAt(q: number, r: number): Habitat | null {
  const h = terrainHeightAt(q, r);
  if (h >= SEA_LEVEL) return null;
  if (habitatOverride) {
    const forced = habitatOverride(q, r);
    if (forced !== null) return forced;
  }
  const depth = SEA_LEVEL - h;
  if (depth >= 3) return 'deep';
  const { q: seedQ, r: seedR } = getTerrainSeed();
  const reef = valueNoise2D((q + seedQ + 555) * 0.16, (r + seedR + 777) * 0.16);
  if (depth <= 2 && reef > 0.63) return 'coral';
  const mang = valueNoise2D((q + seedQ + 999) * 0.13, (r + seedR + 333) * 0.13);
  if (depth === 1 && mang > 0.56) return 'mangrove';
  const shell = valueNoise2D((q + seedQ + 222) * 0.15, (r + seedR + 444) * 0.15);
  if (depth <= 2 && shell > 0.6) return 'shellfish';
  return 'open';
}
